import { createContext, useCallback, useContext, useMemo, useReducer } from "react"
import type { Dispatch } from "react"
import type { Role } from "../model/role"
import useLocalStorage from "../hooks/useLocalStorage"
import type { AplSiteModel, RefCropModel, RemarkModel, StatusModel } from "../api/vegApl"
import { useGetAllRemarks, useGetAllStatus, useGetRefCrops, useGetAllSites, useGetAllPriority } from "../api/vegApl"
import { CROPS_QUERY_CACHE_OPTIONS } from "../api/useGetSortedRefCrops"
import { toTitleCase } from "../utils/HelperFunction";
import { filterStatusesByKey } from "../utils/statusUtils";
import { DEFAULT_PROCESSING_PRIORITIES } from "../components/trail/trialConstants";
// import {useTheme } from "@mui/material"


interface UserDataProps {
    userId: string
    lastName: string
    firstName: string
    accessToken: string
}

interface LoggedInUserDetail {
    is_admin_user: boolean,
    is_tech_user: boolean,
    allocatedSites: string[],
    user_id_db:number
}



interface GlobalStateInterface {
    userId: string
    lastName: string
    firstName: string
    accessToken: string
    searchPlaceholder: string
    searchValue: string
    role: Role
    roleSelectOpen: boolean
    loading: boolean
    ready: boolean
    breadcrumbsText: string[],
    is_admin_user: boolean,
    siteSelected: string,
    roleSelected: string,
    is_tech_user: boolean,
    allocatedSites: any[],
    mobileHome: boolean,
    isArchived: boolean,
    user_id_db:number
}

export interface ICropOption {
    name: string
    value: string
}

type Action =
    | { type: "set_user_data"; data: UserDataProps }
    | { type: "set_search_placeholder"; data: string }
    | { type: "set_search_value"; data: string }
    | { type: "set_role"; data: Role }
    | { type: "set_role_select_open"; data: boolean }
    | { type: "set_ready"; data: boolean }
    | { type: "set_breadcrumbs"; data: string[] }
    | { type: "set_loggin_user_detail"; data: LoggedInUserDetail }
    | { type: "set_global_site"; data: string }
    | { type: "set_global_role"; data: string }
    | { type: "set_mobile_home"; data: boolean }
    | { type: "set_is_archived"; data: boolean }




export const reducer = (state: GlobalStateInterface, action: Action) => {
    switch (action.type) {
        case "set_user_data":
            return {
                ...state,
                userId: action.data.userId,
                lastName: action.data.lastName,
                firstName: action.data.firstName,
                accessToken: action.data.accessToken,
            }
        case "set_search_placeholder":
            return {
                ...state,
                searchPlaceholder: action.data,
            }
        case "set_search_value":
            return {
                ...state,
                searchValue: action.data,
            }
        case "set_role":
            return {
                ...state,
                role: action.data,
            }
        case "set_role_select_open":
            return {
                ...state,
                roleSelectOpen: action.data,
            }
        case "set_ready":
            return {
                ...state,
                ready: action.data,
            }
        case "set_breadcrumbs":
            return {
                ...state,
                breadcrumbsText: action.data,
            }
        case "set_loggin_user_detail":
      
            return {
                ...state,
                is_admin_user: action?.data?.is_admin_user,
                is_tech_user: action?.data?.is_tech_user,
                allocatedSites: action?.data?.allocatedSites ?? [],
                user_id_db:action?.data?.user_id_db
            }
        case "set_global_site":
            return {
                ...state,
                siteSelected: action.data
            }

        case "set_global_role":
            return {
                ...state,
                roleSelected: action.data
            }

        case "set_mobile_home":
            return {
                ...state,
                mobileHome: action.data
            }

        case "set_is_archived":
            return {
                ...state,
                isArchived: action.data
            }

        default:
            return state
    }
}

export const initialState: GlobalStateInterface = {
    userId: "",
    lastName: "",
    firstName: "",
    accessToken: "",
    searchPlaceholder: "Search",
    searchValue: "",
    role: [{ role_id: undefined, role_name: undefined, is_global: undefined, module_id: undefined }],
    roleSelectOpen: false,
    loading: false,
    ready: false,
    breadcrumbsText: [],
    is_admin_user: false,
    siteSelected: "",
    roleSelected: "",
    is_tech_user: false,
    allocatedSites: [],
    mobileHome: false,
    isArchived: false,
    user_id_db:0
}

export interface PriorityOption {
    id: string
    value: string
}

export const GlobalStateContext = createContext({
    state: initialState,
    dispatch: {} as Dispatch<Action>,
    setSearchPlaceholder: (_data: string) => { },
    setSearchValue: (_data: string) => { },
    setRole: (_data: Role) => { },
    setRoleSelectOpen: (_data: boolean) => { },
    setReady: (_data: boolean) => { },
    statuses: [] as StatusModel[],
    trialStatuses: [] as StatusModel[],
    splotStatuses: [] as StatusModel[],
    materialStatuses: [] as StatusModel[],
    crops: [] as RefCropModel[],
    sites: [] as AplSiteModel[],
    cropOptions: [] as ICropOption[],
    remarks: [] as RemarkModel[],
    trialRemarks: [] as RemarkModel[],
    materialRemarks: [] as RemarkModel[],
    reportNoSeedRemarks: [] as RemarkModel[],
    priorities: [] as PriorityOption[],
    setBreadcrumbsText: (_data: string[]) => { },
    setSelectedGlobalSite: (_data: string) => { },
    setSelectedGlobalRole: (_data: string) => { },
    isMobile: false,
    setHomePage: (_data: boolean) => { },
    setIsArchived: (_data: boolean) => { },
})

const noStatus = [] as StatusModel[]
const noCrops = [] as RefCropModel[]
const noRemarks = [] as RemarkModel[]

export const GlobalStateProvider = ({ children }: { children: React.ReactNode }) => {
    //const theme = useTheme();
    const [state, dispatch] = useReducer(reducer, initialState)
    const [lsRole, setLsRole] = useLocalStorage("role", "")
    const setSearchPlaceholder = useCallback(
        (data: string) => dispatch({ type: "set_search_placeholder", data }),
        [dispatch]
    )
    const setSearchValue = useCallback((data: string) => dispatch({ type: "set_search_value", data }), [dispatch])
    const setRole = useCallback(
        (data: Role) => {
            dispatch({ type: "set_role", data })
            setLsRole(data)
        },
        [dispatch, setLsRole]
    )
    const setRoleSelectOpen = useCallback(
        (data: boolean) => dispatch({ type: "set_role_select_open", data }),
        [dispatch]
    )

    const setReady = useCallback((data: boolean) => dispatch({ type: "set_ready", data }), [dispatch])

    const selectedSiteCode = useMemo(() => {
        const site = state.siteSelected?.trim()
        return site ? site : undefined
    }, [state.siteSelected])

    const {
        data: crops = noCrops,
        isFetching: cropsIsLoading = false,
        isError: cropsError,
        isLoadingError: cropsLoadingError,
    } = useGetRefCrops(selectedSiteCode, {
        query: {
            select: (d) => d?.data,
            queryKey: ["crops", selectedSiteCode],
            enabled: state.ready && !!selectedSiteCode,
            keepPreviousData: false,
            ...CROPS_QUERY_CACHE_OPTIONS,
        },
    })
    const cropsLoading = !cropsError && !cropsLoadingError ? cropsIsLoading : false

    const {
        data: trialRemarks = noRemarks,
        isFetching: trialRemarksIsLoading,
    } = useGetAllRemarks('trial', {
        query: {
            select: (d) => d?.data,
            enabled: state.ready,
            staleTime: 60 * 60 * 1000,
            refetchOnReconnect: false,
            refetchOnWindowFocus: false,
            refetchOnMount: false,
        },
    })

    const {
        data: materialRemarks = noRemarks,
        isFetching: materialRemarksIsLoading,
    } = useGetAllRemarks('material', {
        query: {
            select: (d) => d?.data,
            enabled: state.ready,
            staleTime: 60 * 60 * 1000,
            refetchOnReconnect: false,
            refetchOnWindowFocus: false,
            refetchOnMount: false,
        },
    })

    const {
        data: reportNoSeedRemarks = noRemarks,
    } = useGetAllRemarks('report_no_seed', {
        query: {
            select: (d) => d?.data,
            enabled: state.ready,
            staleTime: 60 * 60 * 1000,
            refetchOnReconnect: false,
            refetchOnWindowFocus: false,
            refetchOnMount: false,
        },
    })

    // Keep 'remarks' for backward compatibility - defaults to the material case
    const remarks = materialRemarks

    const { data: sites = [] } = useGetAllSites({
        query: {
            queryKey: ["sites"],
            select: (d) => d.data,
            enabled: state.ready,
            staleTime: 60 * 60 * 1000,
            refetchOnWindowFocus: false,
            refetchOnReconnect: false,
            refetchOnMount: false,
        },
    });

    const { data: apiPriorities = [] as PriorityOption[] } = useGetAllPriority({
        query: {
            queryKey: ["priority"],
            select: (d: any) => {
                const data = d?.data ?? [];
                return data.map((item: any) => ({
                    id: item,
                    value: toTitleCase(item ?? ''),
                }));
            },
            enabled: state.ready,
            staleTime: 60 * 60 * 1000,
            refetchOnReconnect: false,
            refetchOnWindowFocus: false,
            refetchOnMount: false,
        },
    });
    const priorities: PriorityOption[] = DEFAULT_PROCESSING_PRIORITIES.length > 0
        ? [...DEFAULT_PROCESSING_PRIORITIES]
        : apiPriorities;
    const cropOptions: ICropOption[] = useMemo(
        () => [
            {
                name: "Select Crop",//t("select crop", "Select Crop"),
                value: "",
            },
            ...crops
                .map(
                    (o) =>
                        ({
                            name: toTitleCase(o.name as string),
                            value: o.crop_xrd_oid,
                        }) as ICropOption
                )
                .sort((a, b) => a.name.localeCompare(b.name)),
        ],
        [crops]
    )

    const {
        data: statuses = noStatus,
        isFetching: statusesIsLoading = false,
        isError: statusError,
        isLoadingError: statusLoadingError,
    } = useGetAllStatus({
        query: {
            select: (d) => {
                let data = d?.data ?? [];
                const newStatusData = data.map((item: any) => ({
                    ...item,
                    status_value: toTitleCase(item.status_value)
                }));
                return newStatusData.sort((a, b) => a.status_value.localeCompare(b.status_value));

            },
            enabled: state.ready,
            cacheTime: 60 * 60 * 24 * 1000,
            refetchOnReconnect: false,
            refetchOnWindowFocus: false,
            refetchOnMount: false,
        },
    })
    const statusesLoading = !statusError && !statusLoadingError ? statusesIsLoading : false;

    const trialStatuses = useMemo(
        () => filterStatusesByKey(statuses, "trial_status"),
        [statuses]
    )

    const splotStatuses = useMemo(
        () => filterStatusesByKey(statuses, "s2s-splot-status"),
        [statuses]
    )

    const materialStatuses = useMemo(
        () => filterStatusesByKey(statuses, "material_status"),
        [statuses]
    )

    const setBreadcrumbsText = useCallback((data: string[]) => dispatch({ type: "set_breadcrumbs", data }), [dispatch])
    const setSelectedGlobalSite = useCallback((data: string) => dispatch({ type: "set_global_site", data }), [dispatch])
    const setSelectedGlobalRole = useCallback((data: string) => dispatch({ type: "set_global_role", data }), [dispatch])
    const setHomePage = useCallback((data: boolean) => dispatch({ type: "set_mobile_home", data }), [dispatch])
    const setIsArchived = useCallback((data: boolean) => dispatch({ type: "set_is_archived", data }), [dispatch])

    

    let isMobile = window.matchMedia("(max-width: 768px)").matches;
    window.addEventListener("resize", () => {
        isMobile = window.matchMedia("(max-width: 768px)").matches;
        //const isMobile = useMediaQuery(theme.breakpoints.down("sm"), { noSsr: true });
        console.log("Mobile:- resize", isMobile);
    })



    //const isMobile = useMediaQuery(theme.breakpoints.down("sm"), { noSsr: true });



    return (
        <GlobalStateContext.Provider
            value={{
                state: {
                    ...state,
                    loading: cropsLoading || trialRemarksIsLoading || materialRemarksIsLoading || statusesLoading,
                    role: state.role || lsRole,
                },
                crops,
                cropOptions,
                statuses,
                trialStatuses,
                splotStatuses,
                materialStatuses,
                remarks,
                trialRemarks,
                materialRemarks,
                reportNoSeedRemarks,
                sites,
                priorities,
                isMobile,
                dispatch,
                setSearchValue,
                setSearchPlaceholder,
                setRole,
                setRoleSelectOpen,
                setReady,
                setBreadcrumbsText,
                setSelectedGlobalSite,
                setSelectedGlobalRole,
                setHomePage,
                setIsArchived
            }}
        >
            {children}
        </GlobalStateContext.Provider>
    )
}

export const useGlobalState = () => {
    const context = useContext(GlobalStateContext)
    if (!context) {
        throw new Error("useGlobalState must be used within a GlobalStateProvider")
    }
    return context
}
